import { KButton, KSpan, KTitleSpan, KTooltip } from "kahuna-base-react-components"
import React, { useState } from "react"
import { formatIsoDate, hashids } from "../../../utility"
import { Release, Artist, Track } from "../../../types"
import { useNavigate, useParams } from "react-router-dom"
import { lang } from "../../../constants/languages"
import {
  errorFilledToastOptions,
  RELEASE_EDIT_STATUSES,
  RELEASE_STATUSES,
  successFilledToastOptions
} from "../../../constants"
import { toast } from "react-toastify"
import { releases } from "../../../apis"

const ReleaseComponent = ({
  release,
  overrideBorder,
  fullScreen,
  titleOverride,
  canEdit,
  tableComponent,
  track,
  updateReleaseEditStatus
}: {
  release: Release
  overrideBorder: boolean
  fullScreen: boolean
  titleOverride?: string
  canEdit?: boolean
  tableComponent?: boolean
  track?: Track
  updateReleaseEditStatus?: (id: number, editStatus: string) => Promise<boolean>
}) => {
  const [hover, setHover] = useState(false)
  const [editRequestSent, setEditRequestSent] = useState(false)

  const navigate = useNavigate()
  const params = useParams()

  const processUserEditStatus = () => {
    if (release.status !== RELEASE_STATUSES.REVIEWING) {
      return release.status
    }

    if (release.editStatus === RELEASE_EDIT_STATUSES.IN_PROGRESS) {
      return RELEASE_STATUSES.DRAFT
    } else if (release.editStatus === RELEASE_EDIT_STATUSES.SUBMITTED) {
      return RELEASE_STATUSES.REVIEWING
    }

    return release.status
  }

  const getStatusIcon = () => {
    const status = processUserEditStatus()

    if (status === RELEASE_STATUSES.ACTIVE) return "/catalog_icons/active-status.svg"
    else if (status === RELEASE_STATUSES.REVIEWING) return "/catalog_icons/reviewing-status.svg"
    else if (status === RELEASE_STATUSES.ARCHIVED) return "/catalog_icons/archived-status.svg"
    else if (status === RELEASE_STATUSES.DRAFT) return "/catalog_icons/draft-status.svg"
    else return "/catalog_icons/draft-status.svg"
  }

  const getStatusText = () => {
    const releaseStatus = processUserEditStatus()

    const text = lang.catalog.release_status[releaseStatus] || releaseStatus || " - "
    const status = text.slice(0, 1).toUpperCase() + text.slice(1, text.length)

    return `${status}`
  }

  const renderPill = (text: string) => {
    return (
      <div className="flex" style={{ borderRadius: 4, backgroundColor: "#F7F7F7", padding: 4 }} key={Math.random()}>
        <KSpan text={text} fontSize={13} fontWeight={500} lineHeight="12px" color="#111" letterSpacing="0.22px" />
      </div>
    )
  }

  const onClickSmartLink = () => {
    navigate(`/catalog/smart-link/${hashids.encode(release.id?.toString() || "")}`)
  }

  const onClickReleaseMarketing = () => {
    navigate(`/marketing/?search=${release.title}`)
  }

  const renderFullScreenFontSize = () => {
    return tableComponent ? 18 : 24
  }

  const renderFullScreenLineHeight = () => {
    return tableComponent ? "18px" : "24px"
  }

  const onEditAgain = () => {
    if (!release.id || release.status !== RELEASE_STATUSES.REVIEWING || !updateReleaseEditStatus) {
      toast.error(`${lang.common.error}, ${lang.common.contact_customer_support}`, errorFilledToastOptions)
      return
    }

    updateReleaseEditStatus(release.id, RELEASE_EDIT_STATUSES.IN_PROGRESS)
      .then((resp) => {
        if (resp) {
          navigate(`/catalog/asset-registry/${params.releaseId}`)
        } else {
          toast.error(`${lang.common.error}, ${lang.common.contact_customer_support}`, errorFilledToastOptions)
        }
      })
      .catch(() =>
        toast.error(`${lang.common.error}, ${lang.common.contact_customer_support}`, errorFilledToastOptions)
      )
  }

  const onRequestEdit = () => {
    if (!release.id || release.status !== RELEASE_STATUSES.ACTIVE) {
      toast.error(`${lang.common.error}, ${lang.common.contact_customer_support}`, errorFilledToastOptions)
      return
    }

    releases(`/release/request_edit/?release_id=${release.id}`, "POST").then((resp) => {
      if (!resp.data.success) {
        toast.error(`${lang.common.error}, ${lang.common.contact_customer_support}`, errorFilledToastOptions)
        return
      }
      toast.success(lang.catalog.new_release.success_request_edit, successFilledToastOptions)
      setEditRequestSent(true)
    })
  }

  return (
    <div
      data-testid="release-component"
      style={{ width: "100%" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={`rounded-lg p-[16px] ${
        overrideBorder ? "table-row-border-override" : "table-row-hover"
      } cursor-pointer ${tableComponent && "hover:bg-[#F7F7F7] !border-none"} `}
    >
      <div className={`${fullScreen && !tableComponent ? "h-[72px]" : "h-[64px]"} flex justify-between gap-4`}>
        <div className={`flex ${fullScreen && "w-[50%]"} items-center`}>
          <img
            src={release.coverFileUrl}
            className={`${
              fullScreen && !tableComponent ? "w-[80px] h-[80px]" : "w-[72px] h-[72px]"
            } rounded-[6px] cursor-pointer`}
          />
          <div className="flex flex-col justify-center ml-4 gap-1">
            <div className="flex items-center gap-1">
              <KTitleSpan
                text={titleOverride || release.title}
                fontSize={fullScreen ? renderFullScreenFontSize() : 21}
                fontWeight={500}
                lineHeight={fullScreen ? renderFullScreenLineHeight() : "21px"}
              />
              {release.version && <KSpan text={`(${release.version})`} fontSize={14} />}
            </div>
            <div className="flex items-center">
              {!tableComponent && fullScreen && (
                <div className="flex gap-1 items-center mr-2">
                  <img src={getStatusIcon()} className="w-4 h-4" />

                  <KSpan
                    text={`${getStatusText()}: ${formatIsoDate(release.releaseDate)}`}
                    fontSize={13}
                    lineHeight="20px"
                    fontWeight={500}
                    color="#111"
                  />
                </div>
              )}

              {!track && <div>{renderPill(`UPC: ${release.upc}`)}</div>}
            </div>

            <span className="flex gap-1 items-center">
              {renderPill(release.productionYear || " - ")}
              {renderPill(
                `${release.totalTrack} ${
                  release.totalTrack > 1 ? lang.common.song_text_multi : lang.common.song_text_single
                }`
              )}
              {renderPill(release.genre || " - ")}
            </span>
            {track && <div className="flex items-center">{renderPill(`ISRC: ${track.isrc}`)}</div>}
          </div>
        </div>
        {fullScreen && (
          <div className="w-[25%] items-center grid">
            <div className="flex justify-start">
              <div className="flex">
                <div className="grid" style={{ justifyItems: "center" }}>
                  <div className="flex gap-1 flex-wrap">
                    {release.artistsList.map((artist: Artist) => renderPill(artist.name))}
                  </div>
                </div>
              </div>
            </div>
            {(release.featuring?.length || 0) > 0 && (
              <div className="flex justify-start">
                <div className="flex">
                  <div className="grid" style={{ justifyItems: "center" }}>
                    <div className="flex gap-1 flex-wrap">
                      <KSpan text="feat." fontSize={12} />
                      {release?.featuring?.map((feat: string) => renderPill(`${feat}`))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {fullScreen && (
          <div
            className="w-[25%] flex items-center"
            style={{
              justifyContent: tableComponent ? "flex-start" : "flex-end"
            }}
          >
            {tableComponent && (
              <KTooltip
                backgroundColor="#000"
                padding="0px 4px"
                height="20px"
                showArrow
                arrowColor="#000"
                content={
                  <div className="h-5 w-max flex items-center">
                    <KSpan text={formatIsoDate(release.releaseDate)} fontSize={13} lineHeight="16px" color="#FFF" />
                  </div>
                }
              >
                <span className="flex flex-row items-center shrink-0">
                  <img src={getStatusIcon()} className="w-4 h-4 mr-1" />
                  <KSpan text={getStatusText()} fontSize={15} lineHeight="20px" fontWeight={500} color="#111" />
                </span>
              </KTooltip>
            )}

            {!tableComponent && (
              <div
                style={{ alignContent: "center" }}
                className="ml-6"
                onClick={(event) => {
                  event.stopPropagation()
                  onClickSmartLink()
                }}
              >
                <KTooltip
                  backgroundColor={"#595959"}
                  width="100px"
                  position="left"
                  children={
                    <KButton
                      onClick={() => {}}
                      icon="/account_icons/link.svg"
                      background="white"
                      padding="0px"
                      width="36px"
                      height="36px"
                    />
                  }
                  content={<KSpan color="white" text={lang.catalog.release_component.smart_link_tooltip} />}
                />
              </div>
            )}
            {!tableComponent && release.marketingId && (
              <div
                style={{ alignContent: "center" }}
                className="ml-2"
                onClick={(event) => {
                  event.stopPropagation()
                  onClickReleaseMarketing()
                }}
              >
                <KTooltip
                  backgroundColor={"#595959"}
                  width="120px"
                  position="left"
                  children={
                    <KButton
                      onClick={() => {}}
                      icon="/account_icons/soundwave.svg"
                      background="white"
                      padding="0px"
                      width="36px"
                      height="36px"
                    />
                  }
                  content={<KSpan color="white" text={lang.catalog.release_component.marketing_tooltip} />}
                />
              </div>
            )}
            {canEdit && release.status === RELEASE_STATUSES.DRAFT && !tableComponent && (
              <div style={{ alignContent: "center", marginLeft: 8 }}>
                <KButton
                  dataTestId="release-component-edit"
                  height="36px"
                  text={lang.catalog.release_component.edit_button}
                  background="transparent"
                  onClick={() => navigate(`/catalog/asset-registry/${params.releaseId}`)}
                />
              </div>
            )}
            {canEdit && release.status === RELEASE_STATUSES.REVIEWING && !tableComponent && (
              <div style={{ alignContent: "center", marginLeft: 8 }}>
                <KButton
                  dataTestId="release-component-edit-reviewing"
                  height="36px"
                  text={lang.catalog.release_component.edit_reviewing_button}
                  background="transparent"
                  onClick={() => onEditAgain()}
                />
              </div>
            )}
            {canEdit && release.status === RELEASE_STATUSES.ACTIVE && !tableComponent && (
              <div style={{ alignContent: "center", marginLeft: 8 }}>
                <KButton
                  dataTestId="release-component-request-edit"
                  height="36px"
                  text={lang.catalog.release_component.request_edit_button}
                  background="transparent"
                  onClick={() => onRequestEdit()}
                  disabled={editRequestSent}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ReleaseComponent
